import { Injectable } from '@angular/core';
import { BehaviorSubject, bindCallback, firstValueFrom, Observable, Subject } from 'rxjs';
import { map, concatMap, share, tap, finalize, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { User } from '../models/user';
import { UserManagementApiService } from './UserManagementApiService';
import { AppConfig } from '../app.config';
import { BasicResponse } from '../responses/basicResponse';
import { Router } from '@angular/router';
import { Customer } from '../models/customer';
import { Tokens } from '../models/tokens';
import { LoginResponse } from '../responses/loginResponse';
import { LocalStorageMutex } from '../theme/utils/app-localstorage-mutex';
import { SetupTwoFactorResponse } from '../responses/setupTwoFactorResponse';
import { Vendor } from '../models/vendor';
import { CustomerResponse } from '../responses/customerResponse';
import { LoggingService } from './LoggingService';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
   
    public authorizedSubject = new BehaviorSubject<boolean>(false);

    public authenticatedSubject =  new Subject<User|undefined>();
    public tokenReceivedSubject =  new Subject<any>();
    public currentUserSubject = new BehaviorSubject<User|undefined>(undefined);
    public currentCustomerContextSubject = new BehaviorSubject<Customer|undefined>(undefined);
    public currentVendorContextSubject = new BehaviorSubject<Vendor|undefined>(undefined);
    public userMFAChangedSubject = new Subject<{id:number,twoFactorEnabled:boolean} | undefined>();
    public forcePasswordChange?:boolean;
    public forceTwoFactor?:boolean;

    public brandingDataCache:any;
  
    private _oldPassword?:string;
    get oldPassword():string|undefined{
        //only allow to get it once!
        var response=this._oldPassword;
        this._oldPassword=undefined;
        return response;
    }

    private _customerContext?:Customer;
    private _vendorContext?:Vendor;

    private tokensObservableCache?: Observable<Tokens>;

    constructor(private appConfig:AppConfig, private umApiService:UserManagementApiService, private router:Router, private logger:LoggingService) {
        appConfig.initHeaders();
    }

    public init(){
        //if we use session storage to handle a page refresh or similar, then we need to fetch a new token before we can continue.
        var jsonUser=sessionStorage.getItem('currentUser');
        var jsonCustomer=sessionStorage.getItem('currentCustomer');
        var jsonCustomerContext=sessionStorage.getItem('currentCustomerContext');
        var jsonVendor=sessionStorage.getItem('currentVendor');
        var jsonVendorContext=sessionStorage.getItem('currentVendorContext');
        var f2FA=sessionStorage.getItem('f2FA');
        try{
            if(jsonUser && jsonUser!=null){
                var u=User.mapFromDTO(JSON.parse(jsonUser));
                if(jsonCustomer && jsonCustomer!=null){
                    let jsonObj = JSON.parse(jsonCustomer);
                    if(jsonObj.id){
                        //customer always has an empty address string, so check if it actually has an id.
                        var c=Customer.mapFromDTO(jsonObj);
                        u.customer=c;
                    }
                }
                else if(jsonVendor && jsonVendor!=null){
                    let jsonObj = JSON.parse(jsonVendor);
                    if(jsonObj.id){
                        //vendor always has an empty address string, so check if it actually has an id.
                        var v=Vendor.mapFromDTO(jsonObj);
                        u.vendor=v;
                    }
                }
                this.forceTwoFactor = f2FA=="true";
                if(this.appConfig.accessToken && this.appConfig.refreshToken) {
                    if(jsonCustomerContext && jsonCustomerContext!=null) this._customerContext=Customer.mapFromDTO(JSON.parse(jsonCustomerContext));
                    else if(jsonVendorContext && jsonVendorContext!=null) this._vendorContext=Vendor.mapFromDTO(JSON.parse(jsonVendorContext));
                    this.currentUserSubject.next(u);
                    this.authenticatedSubject.next(u);
                    this.authorizedSubject.next(true);
                }
                else {
                    this.authenticatedSubject.next(undefined);
                }
            }
            else this.authenticatedSubject.next(undefined);
        }
        catch(error){
            this.logger.log(error);
            this.authenticatedSubject.next(undefined);
            this.logout();
        }
    }

    public refreshTokens(){
        if (this.tokensObservableCache) return this.tokensObservableCache;
        // New request needed
        else this.tokensObservableCache = this.fetchTokens();
        return this.tokensObservableCache;
    }

     /*
    //old implementation using 'homemade' localStoragemutex; replaced with navigator.locks API
    private fetchTokens(){
        let sub=bindCallback(LocalStorageMutex);
        return sub('refreshToken', 50, 1000).pipe(concatMap(()=>this.umApiService.refreshTokens(this.appConfig.accessToken,this.appConfig.refreshToken).pipe(map((t:Tokens)=>this.processTokensResult(t)), share())));
    }
    */

    private fetchTokens(): Observable<Tokens> {
        return new Observable<Tokens>(observer => {
            navigator.locks.request('refreshToken', async () => {
                const tokens = await firstValueFrom(
                    this.umApiService.refreshTokens(
                        this.appConfig.accessToken,
                        this.appConfig.refreshToken ?? ''
                    ).pipe(map((t: Tokens) => this.processTokensResult(t)))
                );
                observer.next(tokens);
                observer.complete();
            }).catch(err => observer.error(err));
        }).pipe(
            finalize(() => this.tokensObservableCache = undefined), // before shareReplay, so it fires exactly once on the real completion
            shareReplay(1)
        );
    }

    private processTokensResult(tokens:Tokens){
        this.tokensObservableCache=undefined;
        if(tokens){
            this.appConfig.accessToken = tokens.accessToken;
            this.appConfig.refreshToken = tokens.refreshToken
            this.authorizedSubject.next(true);
        }
        else{
            this.logout();
        }
        return tokens;
    }

    //single shared stream so template `(authenticated | async)` bindings don't resubscribe every change-detection pass
    public readonly authenticated$: Observable<boolean> = this.currentUserSubject.pipe(map(u=>u?true:false), distinctUntilChanged(), shareReplay({bufferSize:1, refCount:false}));
    get authenticated(){
        return this.authenticated$;
    }
    get currentUserValue(): User | undefined {
        return this.currentUserSubject.value;
    }
    get isSuperAdmin():boolean | undefined{
        return this.currentUserValue?.isSuperAdmin;
    }
    get isCustomerAdmin():boolean | undefined{
        return this.currentUserValue?.isCustomerAdmin;
    }
    get isVendorAdmin():boolean | undefined{
        return this.currentUserValue?.isVendorAdmin;
    }

    get isSuperAdminContext() {
        return this.isSuperAdmin && !this.customerContext && !this.vendorContext;
    }
    
    get customerContext():Customer|undefined{
        return this.currentUserValue?.role.id==10?this._customerContext:this.currentUserValue?.customer;
    }

    set customerContext(value:Customer|undefined){
        this._customerContext=value;
        if(this._customerContext) sessionStorage.setItem('currentCustomerContext', JSON.stringify(Customer.mapToDTO(this._customerContext)));
        else sessionStorage.removeItem('currentCustomerContext');
        this.currentCustomerContextSubject.next(value);
    }

    get vendorContext():Vendor|undefined{
        return this.currentUserValue?.role.id==10?this._vendorContext:this.currentUserValue?.vendor;
    }

    set vendorContext(value:Vendor | undefined){
        this._vendorContext=value;
        if(this._vendorContext) sessionStorage.setItem('currentVendorContext', JSON.stringify(Vendor.mapToDTO(this._vendorContext)));
        else sessionStorage.removeItem('currentVendorContext');
        this.currentVendorContextSubject.next(value);
    }

    get entityContextId(){
        return this.customerContext?this.customerContext.id:(this.vendorContext?this.vendorContext.id:undefined);
    }
    get entityContextType(){
        return this.customerContext?"C":(this.vendorContext?"V":undefined);
    }

    get hasActivePremierService(){
        return this.premierPartnerIdList?.length>0?true:false;
    }

    get premierPartnerIdList(){
        return this.customerContext?.premierPartnerIdList || this.vendorContext?.premierPartnerIdList || [];
    }

    public login(username: string, password: string) {
        return this.umApiService.loginUser(username, password).pipe(map((r:LoginResponse)=>{
            if(r.success){
                this.appConfig.accessToken = r.tokens.accessToken;
                this.appConfig.refreshToken = r.tokens.refreshToken;
                this._oldPassword=password;
            }
            return r;
        }));
    }

    public finalizeLogin(r:LoginResponse){
        const u = User.mapFromDTO(r.userDTO);
        if(r.customerDTO) u.customer = Customer.mapFromDTO(r.customerDTO);
        if(r.vendorDTO) u.vendor = Vendor.mapFromDTO(r.vendorDTO);
        this.currentUserSubject.next(u);
        if(r.forcePasswordChange){
            this.logger.log("Forcing password change");
            this.forcePasswordChange=true;
            this.router.navigate(['/reset-password']);
        }
        else{
            this.forcePasswordChange=false;
            this.forceTwoFactor = r.forceTwoFactor;
            this.authenticatedSubject.next(u);
            this.authorizedSubject.next(true);
            this.updateSessionAndLocalStorage();
        }
    }

    public logout() {
        // remove user from local storage to log user out
        this.clearSessionAndLocalStorage();
        this.authorizedSubject.next(false);
        this.currentUserSubject.next(undefined);
        this.authenticatedSubject.next(undefined);
        this.customerContext=undefined;
        this.vendorContext=undefined;
        const email=this.currentUserValue?.email;
        if (email) {
            window.localStorage.setItem('CREDENTIALS_FLUSH', email);
            window.localStorage.removeItem('CREDENTIALS_FLUSH');
        }
    }

    public setupTwoFactor(userId: number){
        //shall only be called if user already successfully logged in
        return this.umApiService.setupTwoFactor(userId).pipe(map((r:SetupTwoFactorResponse)=>{
            if(r.success){
                //nothing yet
            }
            return r;
        }));
    }

    public twoFactorEnabled(userId:number){
        if(this.currentUserValue){
            //in case user is logged in then update user data.
            this.currentUserValue.twoFactorEnabled=true;
            this.updateSessionAndLocalStorage();
            this.userMFAChangedSubject.next({id:userId,twoFactorEnabled:true});
        }
    }

    public validateTwoFactor(userId: number, validationKey:string){
        return this.umApiService.validateTwoFactor(userId, validationKey).pipe(map((r:BasicResponse)=>{
            if(r.success){
                //nothing yet
            }
            return r;
        }));
    }

    //only allow to call if user is currently 2FA authenticated
    public removeTwoFactor(userId: number){
        return this.umApiService.removeTwoFactor(userId).pipe(map((r:BasicResponse)=>{
            if(r.success){
                this.currentUserValue!.twoFactorEnabled=false;
                this.updateSessionAndLocalStorage();
                this.userMFAChangedSubject.next({id:userId,twoFactorEnabled:false});
            }
            return r;
        }));
    }

    public recoverTwoFactor(userId:number){
        return this.umApiService.recoverTwoFactor(userId).pipe(map((r:BasicResponse)=>{
            if(r.success){
                //do something
            }
            return r;
        }));
    }

    public validateTwoFactorRecovery(userId: number, recoveryToken:string){
        return this.umApiService.validateTwoFactorRecovery(userId, recoveryToken).pipe(map((r:BasicResponse)=>{
            if(r.success){
                //do something
            }
            return r;
        }));
    }

    public resetPasswordRequest(email:string){
        return this.umApiService.resetPasswordToken(email).pipe(map((r:any)=>{
            return r;
        }));
    }

    public updateUser(u:User):Observable<any>{
        return this.umApiService.updateUser(u).pipe(map((r)=>this.processUpdateUserResult(u, r)));
    }

    private processUpdateUserResult(u:User, r:BasicResponse){
        //we need to update current user to reflect updated profile data.
        if(r.success) {
            this.currentUserSubject.next(u);
            this.updateSessionAndLocalStorage();
        }
        return r;
    }

    public updateCustomer(c:Customer){
        return this.umApiService.updateCustomer(c).pipe(map((r)=>this.processUpdateCustomerResult(c, r)));
    }

    private processUpdateCustomerResult(c:Customer, r:CustomerResponse){
        if(r.success) {
            if(this.currentUserValue!.role.id==20){
                //if currentUser is customerAdmin, then update currentUser
                this.currentUserValue!.customer=c;
                this.updateSessionAndLocalStorage();
            }
        }
        return r;
    }

    public updateVendor(v:Vendor){
        return this.umApiService.updateVendor(v).pipe(map((r)=>this.processUpdateVendorResult(v, r)));
    }

    private processUpdateVendorResult(v:Vendor, r:BasicResponse){
        if(r.success) {
            if(this.currentUserValue!.role.id==50){
                //if currentUser is vendorAdmin, then update currentUser
                this.currentUserValue!.vendor=v;
                this.updateSessionAndLocalStorage();
            }
        }
        return r;
    }

    private updateSessionAndLocalStorage(){
        sessionStorage.setItem('currentUser', JSON.stringify(User.mapToDTO(this.currentUserValue!)));
        sessionStorage.setItem('f2FA', this.forceTwoFactor?'true':'false');
        if(this.currentUserValue!.customer) sessionStorage.setItem('currentCustomer', JSON.stringify(Customer.mapToDTO(this.currentUserValue!.customer)));
        if(this._customerContext) sessionStorage.setItem('currentCustomerContext', JSON.stringify(Customer.mapToDTO(this._customerContext)));
        if(this.currentUserValue!.vendor) sessionStorage.setItem('currentVendor', JSON.stringify(Vendor.mapToDTO(this.currentUserValue!.vendor)));
        if(this._vendorContext) sessionStorage.setItem('currentVendorContext', JSON.stringify(Vendor.mapToDTO(this._vendorContext)));
    }
    
    public clearSessionAndLocalStorage(){
        this.appConfig.clearTokens();
        sessionStorage.clear();
    }
}